import { useContext, useEffect, useRef, useState } from 'react'
import { useMutation } from 'react-query'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageView, PageTopBar, Button, Notification } from '../../components'
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime'
import { CheckCircleIcon } from '../../components/Icons/CheckCircleIcon'
import { ExclamationCircleIcon } from '../../components/Icons/ExclamationCircleIcon'
import { UserContext } from '../../hooks/UserContext'
import { SELECT_WAREHOUSE } from '../../navigation/constants'
import { lookupHandheldPairing, claimHandheldPairing } from '../../services/handheldActivationServices'

const mapErrorToMessage = (err, t) => {
  const status = err?.response?.status
  const code = err?.response?.data?.error
  if (status === 404) return t('handheld_link.errors.not_found')
  // The backend refuses a claim from a standing password identity — only a
  // federated login may bind a device to an account. Without this the raw
  // "sso_required" code would surface as the user-facing message.
  if (status === 403 && code === 'sso_required') return t('handheld_link.errors.sso_required')
  // The code was requested by a different account. Say so plainly: the operator
  // holding the device may be about to be handed a session that is not theirs.
  if (status === 403 && code === 'identity_mismatch') return t('handheld_link.errors.identity_mismatch')
  // The code carries no requesting account: either the handheld's app predates
  // sending one, or the operator typed a username rather than their address.
  if (status === 403 && code === 'identity_unbound') return t('handheld_link.errors.identity_unbound')
  return code || t('handheld_link.errors.generic')
}

// What to do about it, for the failures where there is a next step.
const mapErrorToHint = (err, t) => {
  const status = err?.response?.status
  const code = err?.response?.data?.error
  if (status === 404) return t('handheld_link.errors.not_found_hint')
  if (status === 403 && code === 'sso_required') return t('handheld_link.errors.sso_required_hint')
  if (status === 403 && code === 'identity_mismatch') return t('handheld_link.errors.identity_mismatch_hint')
  if (status === 403 && code === 'identity_unbound') return t('handheld_link.errors.identity_unbound_hint')
  return ''
}

// Codes are shown on the device as uppercase, dash-separated (e.g. H6YJ-P4AX).
// Normalize typing/pasting so casing never causes a false "not found".
const normalizeCode = value => value.toUpperCase().replace(/\s+/g, '')

export const HandheldLinkContainer = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const { user } = useContext(UserContext)
  const goToWms = () => history.push(SELECT_WAREHOUSE)
  const codeFromQuery = normalizeCode(new URLSearchParams(location.search).get('code') || '')

  const [userCode, setUserCode] = useState(codeFromQuery)
  const [pairing, setPairing] = useState(null) // { device_label, requested_at }
  const [linked, setLinked] = useState(false)
  const [lookupError, setLookupError] = useState('')
  const [lookupErrorHint, setLookupErrorHint] = useState('')
  const [notification, setNotification] = useState({ show: false, type: 'success', title: '', description: '' })
  const inputRef = useRef(null)
  const autoLookedUp = useRef(false)

  const lookupMutation = useMutation(() => lookupHandheldPairing(userCode.trim()), {
    onSuccess: data => {
      setPairing(data)
      setLookupError('')
    },
    onError: err => {
      setPairing(null)
      setLookupError(mapErrorToMessage(err, t))
      setLookupErrorHint(mapErrorToHint(err, t))
    },
  })

  const claimMutation = useMutation(() => claimHandheldPairing(userCode.trim()), {
    onSuccess: () => {
      setLinked(true)
      setPairing(null)
      setNotification({ show: true, type: 'success', title: t('handheld_link.success_title'), description: '' })
    },
    onError: err => {
      setNotification({
        show: true,
        type: 'error',
        title: mapErrorToMessage(err, t),
        description: mapErrorToHint(err, t),
      })
    },
  })

  const runLookup = () => {
    setLookupError('')
    setLookupErrorHint('')
    lookupMutation.mutate()
  }

  const handleLookup = e => {
    e.preventDefault()
    if (!userCode.trim() || lookupMutation.isLoading) return
    runLookup()
  }

  // Arriving via QR deep-link (?code=…): look the device up immediately so a
  // scan-then-confirm flow never asks the user to press "Buscar" first.
  useEffect(() => {
    if (codeFromQuery && !autoLookedUp.current) {
      autoLookedUp.current = true
      runLookup()
    } else if (!codeFromQuery) {
      inputRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetToStart = () => {
    setLinked(false)
    setPairing(null)
    setLookupError('')
    setLookupErrorHint('')
    setUserCode('')
    inputRef.current?.focus()
  }

  const backToCode = () => {
    setPairing(null)
    setLookupError('')
    setLookupErrorHint('')
    inputRef.current?.focus()
  }

  const accountLabel = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
  const accountEmail = user?.email

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold text-gray-900">{t('handheld_link.title')}</div>
            <button type="button" onClick={goToWms} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
              {t('handheld_link.go_to_wms')}
            </button>
          </PageTopBar>
        }
        childrenFullWidth={false}
      >
        <div className="mx-auto w-full max-w-md py-6 sm:py-10">
          {/* Success — persistent, so the "return to the device" instruction never disappears */}
          {linked ? (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm sm:p-8">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircleIcon className="h-7 w-7 text-green-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">{t('handheld_link.success_title')}</h2>
              <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-gray-500">{t('handheld_link.success_description')}</p>
              <div className="mt-6 flex flex-col items-center gap-3">
                <Button type="primary" onClick={goToWms} className="w-full sm:w-auto">
                  {t('handheld_link.go_to_wms')}
                </Button>
                <button type="button" onClick={resetToStart} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                  {t('handheld_link.link_another')}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
              {/* Lead: what this screen is and why, in one sentence */}
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <DeviceIcon className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{t('handheld_link.lead_title')}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-gray-500">{t('handheld_link.intro')}</p>
                </div>
              </div>

              {/* Step 1 — enter the code */}
              <form onSubmit={handleLookup} className="mt-6">
                <label htmlFor="handheld-code" className="block text-sm font-medium text-gray-700">
                  {t('handheld_link.step1_title')}
                </label>
                <div className="mt-2 flex items-start gap-2">
                  <div className="flex-1">
                    <input
                      id="handheld-code"
                      ref={inputRef}
                      type="text"
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      placeholder={t('handheld_link.code_placeholder')}
                      value={userCode}
                      onChange={e => setUserCode(normalizeCode(e.target.value))}
                      className="block w-full rounded-md border-gray-300 font-mono text-base uppercase tracking-widest shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <Button
                    buttonType="submit"
                    dense={false}
                    disabled={!userCode.trim()}
                    loading={lookupMutation.isLoading}
                    loading_label={t('handheld_link.lookup_loading')}
                    className="whitespace-nowrap"
                  >
                    {t('handheld_link.lookup_button')}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-gray-400">{t('handheld_link.code_hint')}</p>

                {lookupError && (
                  <div className="mt-3 flex items-start gap-2 rounded-md bg-red-50 p-3">
                    <ExclamationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                    <div>
                      <p className="text-sm font-medium text-red-700">{lookupError}</p>
                      {lookupErrorHint && <p className="mt-0.5 text-xs text-red-600">{lookupErrorHint}</p>}
                    </div>
                  </div>
                )}
              </form>

              {/* Step 2 — confirm: appears once a device is found */}
              {pairing && (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <p className="text-sm font-medium text-gray-700">{t('handheld_link.step2_title')}</p>

                  <div className="mt-3 rounded-lg bg-gray-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-400">{t('handheld_link.device_label')}</p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-900">{pairing.device_label}</p>
                    {pairing.requested_at && (
                      <p className="mt-2 text-xs text-gray-500">
                        {t('handheld_link.requested_at')}: <FormattedRelativeTime date={pairing.requested_at} className="text-gray-600" />
                      </p>
                    )}
                  </div>

                  {(accountLabel || accountEmail) && (
                    <p className="mt-3 text-sm text-gray-500">
                      {t('handheld_link.link_to_account')} <span className="font-medium text-gray-900">{accountLabel || accountEmail}</span>
                      {accountLabel && accountEmail && <span className="text-gray-400"> ({accountEmail})</span>}.
                    </p>
                  )}

                  <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row-reverse sm:items-center">
                    <Button
                      type="primary"
                      onClick={() => claimMutation.mutate()}
                      loading={claimMutation.isLoading}
                      loading_label={t('handheld_link.link_loading')}
                      className="w-full sm:w-auto"
                    >
                      {t('handheld_link.link_button')}
                    </Button>
                    <button
                      type="button"
                      onClick={backToCode}
                      disabled={claimMutation.isLoading}
                      className="text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      {t('handheld_link.search_another')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </PageView>

      <Notification
        show={notification.show}
        setShow={val => setNotification(n => ({ ...n, show: val }))}
        type={notification.type}
        title={notification.title}
        description={notification.description}
      />
    </>
  )
}

const DeviceIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="6" y="2.5" width="12" height="19" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 6h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="18" r="1" fill="currentColor" />
  </svg>
)
